import React, { useEffect, useState, useRef } from "react";
import "./Toast.css";

const Toast = ({ 
  message, 
  type = "info", 
  duration = 4500, 
  onClose, 
  position = "top-right",
  pauseOnHover = true
}) => {
  const [isExiting, setIsExiting] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const timerRef = useRef(null);
  const exitTimerRef = useRef(null);

  // Cleanup all timers on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (exitTimerRef.current) clearTimeout(exitTimerRef.current);
    };
  }, []);

  // Auto-close after duration
  useEffect(() => {
    if (duration === 0) return; // Infinite duration (manual close only)

    const startTimer = () => {
      timerRef.current = setTimeout(() => {
        handleClose();
      }, duration);
    };

    if (!isPaused) {
      startTimer();
    }

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [duration, isPaused]);

  const handleClose = () => {
    if (isExiting) return;
    
    setIsExiting(true);
    exitTimerRef.current = setTimeout(() => {
      onClose();
    }, 300); // Wait for exit animation
  };

  const getIcon = () => {
    switch (type) {
      case 'success': return '✅';
      case 'error': return '❌';
      case 'warning': return '⚠️';
      case 'info': return 'ℹ️';
      default: return '💡';
    }
  };

  const getAriaLabel = () => {
    switch (type) {
      case 'success': return 'Success notification';
      case 'error': return 'Error notification';
      case 'warning': return 'Warning notification';
      case 'info': return 'Information notification';
      default: return 'Notification';
    }
  };

  const getRole = () => {
    switch (type) {
      case 'error': return 'alert';
      case 'warning': return 'alert';
      default: return 'status';
    }
  };

  return (
    <div 
      className={`toast-wrapper toast-wrapper-${position} ${isExiting ? 'toast-exiting' : 'toast-entering'}`}
      onMouseEnter={() => pauseOnHover && setIsPaused(true)}
      onMouseLeave={() => pauseOnHover && setIsPaused(false)}
      role={getRole()}
      aria-label={getAriaLabel()}
      aria-live={type === 'error' ? 'assertive' : 'polite'}
    >
      <div className={`toast toast-${type}`}>
        <div className="toast-content">
          <div className="toast-icon-wrapper" aria-hidden="true">
            <span className="toast-icon">
              {getIcon()}
            </span>
          </div>
          
          <div className="toast-message-wrapper">
            <p className="toast-message">{message}</p>
          </div>
          
          <button 
            className="toast-close"
            onClick={handleClose}
            aria-label={`Close ${getAriaLabel()}`}
            type="button"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
              <path d="M1 1L13 13M13 1L1 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
        </div>
        {duration > 0 && (
          <div 
            className="toast-progress" 
            style={{ 
              '--duration': `${duration}ms`,
              animationPlayState: isPaused ? 'paused' : 'running'
            }}
          ></div>
        )}
      </div>
    </div>
  );
};

export default Toast;